import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Button,
  Menu,
} from '@mui/material';
import { Inventory, SearchOutlined, FilterListOffOutlined, FileDownloadOutlined, PictureAsPdfOutlined, TableChartOutlined } from '@mui/icons-material';
import { api } from '../../services/api';
import { InventoryItem, Category } from '../../types/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

type StockStatus = 'all' | 'in_stock' | 'low_stock' | 'out_of_stock';

const StockList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize filters from URL
  const initialStatus = (searchParams.get('status') as StockStatus) || 'all';
  const initialCategory = searchParams.get('category') || 'all';
  const initialSearch = searchParams.get('search') || '';

  const [stock, setStock] = useState<InventoryItem[]>([]);
  const [filteredStock, setFilteredStock] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);

  // Filters
  const [searchName, setSearchName] = useState(initialSearch);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);
  const [selectedStatus, setSelectedStatus] = useState<StockStatus>(initialStatus);

  // Export menu
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchStock();
    fetchCategories();
  }, []);

  useEffect(() => {
    applyFilters();
    // Sync filters to URL
    const params: Record<string, string> = {};
    if (searchName) params.search = searchName;
    if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
    if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus;
    setSearchParams(params, { replace: true });
  }, [searchName, selectedCategory, selectedStatus, stock]);

  const fetchStock = async () => {
    try {
      const res = await api.get('/api/items/stock');
      // Normalize response to an array. API may return { stocks: [...] } or an array directly.
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.stocks)
        ? res.data.stocks
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setStock(data);
    } catch (err) {
      console.error('Error fetching stock:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/categories');
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.categories)
        ? res.data.categories
        : [];
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const getStatus = (qty: number, minStock: number = 0): StockStatus => {
    if (qty <= 0) return 'out_of_stock';
    if (qty < minStock) return 'low_stock';
    return 'in_stock';
  };

  const applyFilters = () => {
    // Guard against non-iterable `stock` values
    let filtered = Array.isArray(stock) ? [...stock] : [];

    // Filter by name
    if (searchName.trim()) {
      const query = searchName.toLowerCase().trim();
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter((item) => {
        const status = getStatus(item.available_quantity, item.min_stock);
        return status === selectedStatus;
      });
    }

    setFilteredStock(filtered);
  };

  const clearFilters = () => {
    setSearchName('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = searchName || selectedCategory !== 'all' || selectedStatus !== 'all';

  const getStatusLabel = (status: StockStatus): string => {
    switch (status) {
      case 'in_stock':
        return 'In Stock';
      case 'low_stock':
        return 'Low Stock';
      case 'out_of_stock':
        return 'Out of Stock';
      default:
        return '';
    }
  };

  const getStatusColor = (status: StockStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'in_stock':
        return 'success';
      case 'low_stock':
        return 'warning';
      case 'out_of_stock':
        return 'error';
      default:
        return 'default';
    }
  };

  // Export functions
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const getExportData = () => {
    return filteredStock.map((item) => {
      const status = getStatus(item.available_quantity, item.min_stock);
      return {
        'Item Name': item.name,
        'Category': item.category || '-',
        'Quantity': item.available_quantity,
        'Status': getStatusLabel(status),
      };
    });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const data = getExportData();
    const itemsPerPage = 48;
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const pageHeight = doc.internal.pageSize.height;

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) {
        doc.addPage();
      }

      const startIdx = page * itemsPerPage;
      const endIdx = Math.min(startIdx + itemsPerPage, data.length);
      const pageData = data.slice(startIdx, endIdx);

      // Add title (compact header)
      doc.setFontSize(12);
      doc.text('Stock Report', 12, 12);
      doc.setFontSize(8);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 12, 17);

      // Add table with compact styling for 45 items per page
      autoTable(doc, {
        head: [['Item Name', 'Category', 'Quantity', 'Status']],
        body: pageData.map((item) => [
          item['Item Name'],
          item['Category'],
          item['Quantity'].toString(),
          item['Status'],
        ]),
        startY: 22,
        styles: { 
          fontSize: 8, 
          cellPadding: 1.0,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: { 
          fillColor: [255, 255, 255], 
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          cellPadding: 1,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
        },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        margin: { bottom: 10 },
      });

      // Add footer with page number (small, at bottom)
      doc.setFontSize(7);
      doc.setTextColor(128);
      doc.text(
        `Page ${page + 1} of ${totalPages}`,
        doc.internal.pageSize.width / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.setTextColor(0);
    }

    const today = new Date();
    const dateStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    doc.save(`stock-report-${dateStr}.pdf`);
    handleExportClose();
  };

  const exportToExcel = () => {
    const data = getExportData();
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Report');
    
    // Auto-size columns
    const colWidths = [
      { wch: 30 }, // Item Name
      { wch: 20 }, // Category
      { wch: 12 }, // Quantity
      { wch: 15 }, // Status
    ];
    worksheet['!cols'] = colWidths;

    const today = new Date();
    const dateStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    XLSX.writeFile(workbook, `stock-report-${dateStr}.xlsx`);
    handleExportClose();
  };

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Inventory sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Current Stock Levels
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlined />}
          onClick={handleExportClick}
          disabled={filteredStock.length === 0}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={handleExportClose}
        >
          <MenuItem onClick={exportToPDF}>
            <PictureAsPdfOutlined sx={{ mr: 1 }} />
            Export as PDF
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <TableChartOutlined sx={{ mr: 1 }} />
            Export as Excel
          </MenuItem>
        </Menu>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Search by Item Name */}
        <TextField
          size="small"
          placeholder="Search by item name..."
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Category Filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.name}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Stock Status Filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Stock Status</InputLabel>
          <Select
            value={selectedStatus}
            label="Stock Status"
            onChange={(e) => setSelectedStatus(e.target.value as StockStatus)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="in_stock">In Stock</MenuItem>
            <MenuItem value="low_stock">Low Stock (Below Min)</MenuItem>
            <MenuItem value="out_of_stock">Out of Stock</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="text"
            startIcon={<FilterListOffOutlined />}
            onClick={clearFilters}
            size="small"
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Showing {filteredStock.length} of {stock.length} items
      </Typography>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  Quantity
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStock.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? 'No items match your filters'
                        : 'No stock data available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStock.map((item) => {
                  const status = getStatus(item.available_quantity, item.min_stock);
                  return (
                    <TableRow key={item.item_id} hover>
                      <TableCell>
                        <Typography 
                          variant="body2"
                          fontWeight="medium"
                          onClick={() => navigate(`/items/${item.item_id}`)}
                          sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.category || '-'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={status === 'out_of_stock' ? 'error.main' : 'inherit'}
                        >
                          {item.available_quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(status)}
                          color={getStatusColor(status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            {filteredStock.length > 0 && (
              <TableBody>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={2} align="right">
                    <Typography variant="body2" fontWeight="bold">
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {filteredStock.reduce((sum, item) => sum + (item.available_quantity || 0), 0)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default StockList;
